import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Drawer, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import CloseIcon from '@material-ui/icons/Close';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import HttpService from '../../services/httpService';

const useStyles = makeStyles(() => ({
    paper: { width: 440, maxWidth: '100%' },
    container: { height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f4f6f9' },
    header: {
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', flexShrink: 0, color: '#fff',
        background: 'linear-gradient(135deg, #1C375D 0%, #27497a 100%)',
    },
    avatar: {
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.16)',
    },
    name: { fontSize: 15, fontWeight: 600, lineHeight: 1.3 },
    meta: { fontSize: 12, opacity: 0.85, marginTop: 2 },
    close: { color: '#fff', marginLeft: 'auto', marginTop: -4, marginRight: -6 },
    section: { padding: '14px 16px 6px', flexShrink: 0 },
    sectionTitle: { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#5f7a95', marginBottom: 8 },
    audio: { width: '100%', height: 40 },
    empty: { fontSize: 13, color: '#78909c', padding: '6px 0' },
    error: { fontSize: 13, color: '#c62828' },
    transcript: { flex: 1, overflowY: 'auto', padding: '4px 16px 20px' },
    turn: { display: 'flex', flexDirection: 'column', marginBottom: 10 },
    agentTurn: { alignItems: 'flex-start' },
    customerTurn: { alignItems: 'flex-end' },
    speaker: { fontSize: 11, fontWeight: 600, color: '#78909c', marginBottom: 3, padding: '0 4px' },
    bubble: {
        maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', boxShadow: '0 1px 1.5px rgba(0,0,0,0.08)',
    },
    agentBubble: { backgroundColor: '#fff', color: '#263238', borderTopLeftRadius: 4 },
    customerBubble: { backgroundColor: '#1C375D', color: '#fff', borderTopRightRadius: 4 },
    time: { fontSize: 10.5, color: '#90a4ae', marginTop: 3, padding: '0 4px' },
    note: {
        alignSelf: 'center', margin: '2px 0 4px', padding: '3px 10px', borderRadius: 999,
        backgroundColor: '#e3e8ee', color: '#546e7a', fontSize: 11, fontWeight: 600,
    },
}));

const formatDuration = (seconds) => {
    const total = Math.round(seconds);
    return total >= 60 ? `${Math.floor(total / 60)}m ${total % 60}s` : `${total}s`;
};

const EMPTY = { loading: false, error: '', recording: '', transcript: [] };

// Calling Desk > Results side panel: the call's recording plays here and its
// transcript reads below it (GET callingDesk/results/:id/call).
const CallDetailsDrawer = ({ row, onClose }) => {
    const classes = useStyles();
    const [call, setCall] = useState(EMPTY);

    useEffect(() => {
        if (!row?._id) return undefined;
        let cancelled = false;
        setCall({ ...EMPTY, loading: true, recording: row.call?.recording || '' });
        HttpService.getCallingDeskResultCall(row._id)
            .then((response) => {
                if (cancelled) return;
                setCall({
                    ...EMPTY,
                    recording: response?.data?.recording || row.call?.recording || '',
                    transcript: response?.data?.transcript || [],
                });
            })
            .catch((error) => {
                if (cancelled) return;
                setCall((current) => ({ ...current, loading: false, error: error?.response?.data?.message || 'Could not load this call' }));
            });
        return () => { cancelled = true; };
    }, [row]);

    const customerName = row?.customerName || 'Customer';

    return (
        <Drawer anchor="right" open={Boolean(row)} onClose={onClose} classes={{ paper: classes.paper }}>
            {row && (
                <div className={classes.container}>
                    <div className={classes.header}>
                        <div className={classes.avatar}><PhoneInTalkIcon fontSize="small" /></div>
                        <div>
                            <div className={classes.name}>{customerName}</div>
                            <div className={classes.meta}>
                                {[row.callTypeLabel || row.callType, row.leadNo || row.uniqueLeadNumber].filter(Boolean).join(' · ')}
                            </div>
                            <div className={classes.meta}>
                                {[
                                    moment(row.call?.startedAt || row.createdAt).format('DD MMM YYYY, hh:mm A'),
                                    row.call?.duration ? formatDuration(row.call.duration) : '',
                                ].filter(Boolean).join(' · ')}
                            </div>
                        </div>
                        <IconButton size="small" className={classes.close} onClick={onClose} aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.section}>
                        <div className={classes.sectionTitle}>Recording</div>
                        {call.recording
                            ? <audio key={call.recording} className={classes.audio} controls preload="metadata" src={call.recording} />
                            : <div className={classes.empty}>{call.loading ? 'Loading…' : 'No recording for this call'}</div>}
                    </div>

                    <div className={classes.section}>
                        <div className={classes.sectionTitle}>Transcript</div>
                    </div>
                    <div className={classes.transcript}>
                        {call.loading && [85, 60, 78, 55].map((width, index) => (
                            <Skeleton
                                key={width}
                                variant="rect"
                                height={36}
                                style={{ width: `${width}%`, borderRadius: 10, marginBottom: 10, marginLeft: index % 2 ? 'auto' : 0 }}
                            />
                        ))}
                        {!call.loading && call.error && <div className={classes.error}>{call.error}</div>}
                        {!call.loading && !call.error && call.transcript.length === 0 && (
                            <div className={classes.empty}>No transcript for this call</div>
                        )}
                        {!call.loading && call.transcript.map((turn, index) => {
                            if (turn.role === 'note') {
                                return <div key={index} className={classes.turn}><span className={classes.note}>{turn.content}</span></div>;
                            }
                            const isCustomer = turn.role === 'user';
                            return (
                                <div key={index} className={`${classes.turn} ${isCustomer ? classes.customerTurn : classes.agentTurn}`}>
                                    <div className={classes.speaker}>{isCustomer ? customerName : 'AI agent'}</div>
                                    <div className={`${classes.bubble} ${isCustomer ? classes.customerBubble : classes.agentBubble}`}>{turn.content}</div>
                                    {turn.timestamp && <div className={classes.time}>{moment(turn.timestamp).format('hh:mm:ss A')}</div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Drawer>
    );
};

export default CallDetailsDrawer;
